import React, { useState, useEffect, useCallback } from "react"
import { SpinnerText } from "../../ui/Spinner"
import {
  generatePreview,
  validateTrademarkImageFile,
  fileToBase64
} from "../../../utils/fileUploadUtils"
import ConfirmDialog from "../../ui/ConfirmDialog"
import FileUploadBox from "../../ui/FileUploadBox"
import { uploadDocument } from "../../../services/documentService"
import { formatApiError } from "../../../utils/formatApiError"

type FigurativeMarkFormProps = {
  onCancel: () => void
  onSave: (data: {
    imageFile: File | null
    text?: string
    colorClaim?: string
    category: string
    euipoDocumentId?: string
    euipoConstitutionDocumentId?: string
    imageData?: string
    contentType?: string
  }) => void
  initialData?: {
    imageFile?: File | null
    text?: string
    colorClaim?: string
    category: string
    euipoDocumentId?: string
    euipoConstitutionDocumentId?: string
    imageData?: string
    contentType?: string
  }
  disabled?: boolean
}

export default function FigurativeMarkForm({
  onCancel,
  onSave,
  initialData,
  disabled
}: FigurativeMarkFormProps) {
  const [category] = useState(initialData?.category || "individualMark")
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState("")
  const [uploading, setUploading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const [textInMark, setTextInMark] = useState(initialData?.text || "")
  const [colorClaim, setColorClaim] = useState(initialData?.colorClaim || "")

  const [euipoDocumentId, setEuipoDocumentId] = useState<string | undefined>(
    initialData?.euipoDocumentId
  )

  useEffect(() => {
    if (initialData?.imageData) {
      setPreviewUrl(initialData.imageData)
    }
  }, [initialData])

  const uploadAndSetId = useCallback(async (file: File) => {
    setUploading(true)
    try {
      const result = await uploadDocument(file, "MARK_IMAGE")
      if (result.euipoDocumentId) {
        setEuipoDocumentId(result.euipoDocumentId)
        setError("")
      } else {
        setError("Upload succeeded but no document ID returned")
      }
    } catch (error) {
      console.error("Upload error:", error)
      setError(
        formatApiError(
          error,
          "The image could not be uploaded. Please try again."
        )
      )
      setUploadedFile(null)
      setPreviewUrl(null)
      setEuipoDocumentId(undefined)
    } finally {
      setUploading(false)
    }
  }, [])

  const handleFileSelect = async (file: File) => {
    const validationError = await validateTrademarkImageFile(file)
    if (validationError) {
      setError(validationError)
      setUploadedFile(null)
      setPreviewUrl(null)
      setEuipoDocumentId(undefined)
      return
    }

    setError("")
    setUploadedFile(file)

    const preview = await generatePreview(file)
    setPreviewUrl(preview)

    await uploadAndSetId(file)
  }

  const confirmDelete = () => {
    setUploadedFile(null)
    setPreviewUrl(null)
    setEuipoDocumentId(undefined)
    setError("")
    setShowConfirm(false)
  }

  const handleSave = async () => {
    if (error) return

    if (uploading) {
      setError("Please wait until the upload is finished.")
      return
    }

    if (!euipoDocumentId) {
      setError("Please upload a valid JPG image before saving.")
      return
    }

    let base64Data: string | undefined
    let fileContentType: string | undefined

    if (uploadedFile) {
      const rawBase64 = await fileToBase64(uploadedFile)

      base64Data = rawBase64.startsWith("data:")
        ? rawBase64
        : `data:${uploadedFile.type};base64,${rawBase64}`

      fileContentType = uploadedFile.type
    } else if (initialData?.imageData) {
      base64Data = initialData.imageData
      fileContentType = initialData.contentType
    }

    onSave({
      imageFile: uploadedFile,
      text: textInMark || undefined,
      colorClaim: colorClaim || undefined,
      category,
      euipoDocumentId,
      imageData: base64Data,
      contentType: fileContentType
    })
  }

  return (
    <div className="border rounded-md p-4 bg-white mt-6">
      <h3 className="text-lg font-semibold mb-3">Figurative Mark</h3>

      <div className="border border-blue-200 bg-blue-50 rounded-md p-3 mb-4 text-sm">
        <strong>Note:</strong> This type of mark contains graphic and/or word
        elements.
      </div>

      <FileUploadBox
        uploadedFile={uploadedFile}
        previewUrl={previewUrl}
        error={error}
        onFileSelect={handleFileSelect}
        onDelete={() => setShowConfirm(true)}
        accept=".jpg,.jpeg"
        helperText="JPG, max. 2 MB, maximum image size 2,835 x 2,010 pixels."
        disabled={disabled}
      />

      <label className="block text-sm font-medium mt-3">
        Text in the mark (optional)
      </label>
      <input
        type="text"
        value={textInMark}
        onChange={(e) => setTextInMark(e.target.value)}
        disabled={disabled}
        className={`w-full border border-gray-300 rounded-md px-3 py-2 mb-3 focus:ring-1 focus:ring-orange-400 ${
          disabled ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      />

      <label className="block text-sm font-medium">
        Color claim (optional)
      </label>
      <input
        type="text"
        value={colorClaim}
        onChange={(e) => setColorClaim(e.target.value)}
        disabled={disabled}
        className={`w-full border border-gray-300 rounded-md px-3 py-2 mb-4 focus:ring-1 focus:ring-orange-400 ${
          disabled ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      />

      <div className="flex justify-end gap-4 mt-6">
        <button
          onClick={onCancel}
          className={`text-sm text-gray-600 hover:text-gray-800 ${
            disabled ? "cursor-not-allowed opacity-50" : ""
          }`}
          disabled={disabled}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className={`text-sm px-4 py-2 rounded ${
            disabled
              ? "bg-gray-300 text-white cursor-not-allowed"
              : uploading
                ? "bg-orange-300 text-white cursor-wait"
                : "bg-orange-500 text-white hover:bg-orange-600"
          }`}
          disabled={disabled || uploading}
        >
          {uploading ? <SpinnerText>Uploading...</SpinnerText> : "Save"}
        </button>
      </div>

      {showConfirm && (
        <ConfirmDialog
          message="Do you really want to delete this file?"
          onCancel={() => setShowConfirm(false)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  )
}
